import type { MatrixClient } from 'matrix-js-sdk'
import { rewriteMatrixHomeserverUrl } from '@/modules/chat-matrix-fast/config'
import {
  ensureMatrixSession,
  refreshMatrixAccessToken,
} from '@/modules/chat-matrix-fast/matrixSession'
import {
  avatarDebugLog,
  summarizeAvatarUrlForLog,
} from '@/modules/chat-matrix-fast/utils/chatDebugLog'

/**
 * In-memory LRU for Matrix media blobs.
 * Refs track on-screen consumers; when refs hit 0 the blob stays parked
 * so switching chats does not re-download / flicker avatars.
 *
 * Oversized blobs (> MAX_CACHEABLE_BLOB_BYTES) are never idle-parked: a 49MB
 * full `/download` would otherwise be inserted with refs=0, immediately
 * LRU-evicted (idle cap 25MB), then "miss after download → retry" forever.
 */
const MAX_IDLE_ENTRIES = 150
const MAX_IDLE_BYTES = 25 * 1024 * 1024
/** Do not keep idle blobs larger than this (avatars should be thumbnails). */
const MAX_CACHEABLE_BLOB_BYTES = 2 * 1024 * 1024

interface BlobCacheEntry {
  blobUrl: string
  /** Byte size; `-1` means invalidated while still referenced. */
  size: number
  refs: number
  /** If true, revoke as soon as refs hit 0 (never park idle). */
  noIdlePark?: boolean
}

/** Insertion order = LRU (oldest first). Touch = delete+set. */
const blobUrlCache = new Map<string, BlobCacheEntry>()
const blobUrlPending = new Map<string, Promise<string>>()

export function isMatrixMediaHttpUrl(url: string): boolean {
  return url.includes('/_matrix/media/')
    || url.includes('/_matrix/client/v1/media/')
}

/**
 * Single point of truth for the media URL rewrite:
 * legacy `media/v3` → authenticated `client/v1/media`, then homeserver rewrite.
 * Callers guard with `isMatrixMediaHttpUrl` where non-Matrix URLs must pass through untouched.
 */
export function rewriteMatrixMediaHttpUrl(httpUrl: string): string {
  return rewriteMatrixHomeserverUrl(
    httpUrl.includes('/_matrix/media/v3/')
      ? httpUrl.replace('/_matrix/media/v3/', '/_matrix/client/v1/media/')
      : httpUrl,
  )
}

export function toAuthenticatedMatrixMediaUrl(
  matrixClient: MatrixClient,
  file: string,
  width?: number,
  height?: number,
  resizeMethod?: string,
): string {
  if (file.startsWith('mxc://') || file.startsWith('mxc:')) {
    return matrixClient.mxcUrlToHttp(
      file,
      width,
      height,
      resizeMethod,
      false,
      true,
      true,
    ) ?? file
  }

  return rewriteMatrixMediaHttpUrl(file)
}

const AVATAR_THUMB_WIDTH = 96
const AVATAR_THUMB_HEIGHT = 96
const POSTER_THUMB_WIDTH = 480
const POSTER_THUMB_HEIGHT = 270

/** Avatar-sized Matrix media URL — thumbnail, not multi‑MB /download into LRU. */
export function toAvatarMatrixMediaUrl(
  matrixClient: MatrixClient,
  file: string,
  width = AVATAR_THUMB_WIDTH,
  height = AVATAR_THUMB_HEIGHT,
): string {
  if (file.startsWith('mxc://') || file.startsWith('mxc:')) {
    return toAuthenticatedMatrixMediaUrl(matrixClient, file, width, height, 'crop')
  }

  if (!isMatrixMediaHttpUrl(file)) {
    return file
  }

  return rewriteMatrixDownloadToThumbnail(rewriteMatrixMediaHttpUrl(file), width, height, 'crop')
}

/** Video/image poster via Matrix /thumbnail/, never full /download/. */
export function toPosterMatrixMediaUrl(
  matrixClient: MatrixClient,
  file: string,
  width = POSTER_THUMB_WIDTH,
  height = POSTER_THUMB_HEIGHT,
): string {
  if (file.startsWith('mxc://') || file.startsWith('mxc:')) {
    return toAuthenticatedMatrixMediaUrl(matrixClient, file, width, height, 'scale')
  }

  if (!isMatrixMediaHttpUrl(file)) {
    return file
  }

  return rewriteMatrixDownloadToThumbnail(rewriteMatrixMediaHttpUrl(file), width, height, 'scale')
}

function rewriteMatrixDownloadToThumbnail(
  httpUrl: string,
  width: number,
  height: number,
  method: 'crop' | 'scale' = 'crop',
): string {
  const downloadMarker = '/media/download/'
  const thumbMarker = '/media/thumbnail/'
  if (!httpUrl.includes(downloadMarker)) {
    return httpUrl
  }

  try {
    const url = new URL(httpUrl, typeof window !== 'undefined' ? window.location.origin : 'https://local')
    url.pathname = url.pathname.replace(downloadMarker, thumbMarker)
    url.searchParams.set('width', String(width))
    url.searchParams.set('height', String(height))
    url.searchParams.set('method', method)
    // Keep path+search relative if input was relative
    if (httpUrl.startsWith('/')) {
      return `${url.pathname}${url.search}`
    }
    return url.toString()
  } catch {
    const base = httpUrl.replace(downloadMarker, thumbMarker)
    const sep = base.includes('?') ? '&' : '?'
    return `${base}${sep}width=${width}&height=${height}&method=${method}`
  }
}

/** Fallback when Synapse cannot thumbnail a media id (400 on /thumbnail/). */
function rewriteMatrixThumbnailToDownload(httpUrl: string): string {
  const thumbMarker = '/media/thumbnail/'
  const downloadMarker = '/media/download/'
  if (!httpUrl.includes(thumbMarker)) {
    return httpUrl
  }

  try {
    const url = new URL(httpUrl, typeof window !== 'undefined' ? window.location.origin : 'https://local')
    url.pathname = url.pathname.replace(thumbMarker, downloadMarker)
    url.searchParams.delete('width')
    url.searchParams.delete('height')
    url.searchParams.delete('method')
    if (httpUrl.startsWith('/')) {
      return `${url.pathname}${url.search}`
    }
    return url.toString()
  } catch {
    return httpUrl
      .replace(thumbMarker, downloadMarker)
      .replace(/[?&]width=\d+/g, '')
      .replace(/[?&]height=\d+/g, '')
      .replace(/[?&]method=[^&]*/g, '')
      .replace(/\?&/, '?')
      .replace(/[?&]$/, '')
  }
}

function isUsableEntry(entry: BlobCacheEntry | undefined): entry is BlobCacheEntry {
  return Boolean(entry && entry.size >= 0)
}

function touchCacheEntry(httpUrl: string): BlobCacheEntry | undefined {
  const entry = blobUrlCache.get(httpUrl)
  if (!isUsableEntry(entry)) {
    return undefined
  }
  blobUrlCache.delete(httpUrl)
  blobUrlCache.set(httpUrl, entry)
  return entry
}

function revokeEntry(httpUrl: string, entry: BlobCacheEntry, reason: string): void {
  avatarDebugLog('blob · revoke', {
    reason,
    httpUrl: summarizeAvatarUrlForLog(httpUrl),
    size: entry.size,
    refs: entry.refs,
    cacheSize: blobUrlCache.size,
  })
  URL.revokeObjectURL(entry.blobUrl)
  blobUrlCache.delete(httpUrl)
}

/** Evict oldest idle (refs=0) entries when over size/count caps. */
function evictIdleIfNeeded(protectKey?: string): void {
  // Drop oversized idle entries first — they must never stay parked.
  for (const [httpUrl, entry] of [...blobUrlCache.entries()]) {
    if (entry.refs > 0 || entry.size < 0) {
      continue
    }
    if (entry.noIdlePark || entry.size > MAX_CACHEABLE_BLOB_BYTES) {
      if (protectKey && httpUrl === protectKey) {
        continue
      }
      revokeEntry(httpUrl, entry, 'oversized-idle')
    }
  }

  let idleCount = 0
  let idleBytes = 0
  for (const entry of blobUrlCache.values()) {
    if (entry.refs > 0 || entry.size < 0) {
      continue
    }
    idleCount += 1
    idleBytes += entry.size
  }

  if (idleCount <= MAX_IDLE_ENTRIES && idleBytes <= MAX_IDLE_BYTES) {
    return
  }

  for (const [httpUrl, entry] of blobUrlCache) {
    if (entry.refs > 0 || entry.size < 0) {
      continue
    }
    if (protectKey && httpUrl === protectKey) {
      continue
    }
    if (idleCount <= MAX_IDLE_ENTRIES && idleBytes <= MAX_IDLE_BYTES) {
      break
    }
    revokeEntry(httpUrl, entry, 'lru-evict')
    idleCount -= 1
    idleBytes -= entry.size
  }
}

function parkBlobUrl(httpUrl: string, blobUrl: string, size: number): string {
  const noIdlePark = size > MAX_CACHEABLE_BLOB_BYTES
  if (noIdlePark) {
    avatarDebugLog('blob · oversized · no idle park', {
      httpUrl: summarizeAvatarUrlForLog(httpUrl),
      size,
      maxCacheable: MAX_CACHEABLE_BLOB_BYTES,
    })
  }

  const existing = blobUrlCache.get(httpUrl)
  if (existing) {
    // Fresh usable entry already there (race) — keep it, drop the new blob.
    if (isUsableEntry(existing) && existing.blobUrl !== blobUrl) {
      URL.revokeObjectURL(blobUrl)
      blobUrlCache.delete(httpUrl)
      blobUrlCache.set(httpUrl, existing)
      return existing.blobUrl
    }

    // Replace stale / same-key entry; preserve live refs.
    if (existing.blobUrl !== blobUrl) {
      URL.revokeObjectURL(existing.blobUrl)
    }
    existing.blobUrl = blobUrl
    existing.size = size
    existing.noIdlePark = noIdlePark
    blobUrlCache.delete(httpUrl)
    blobUrlCache.set(httpUrl, existing)
    // Protect this key so a just-downloaded oversized blob is not self-evicted
    evictIdleIfNeeded(httpUrl)
    return blobUrl
  }

  blobUrlCache.set(httpUrl, { blobUrl, size, refs: 0, noIdlePark })
  evictIdleIfNeeded(httpUrl)
  return blobUrl
}

function retainBlobUrl(httpUrl: string, blobUrl: string): string {
  const entry = touchCacheEntry(httpUrl)
  if (entry) {
    entry.refs += 1
    return entry.blobUrl
  }

  blobUrlCache.set(httpUrl, { blobUrl, size: 0, refs: 1 })
  return blobUrl
}

/** Sync peek for warm UI start (no retain). */
export function peekMatrixMediaBlobUrl(httpUrl: string): string | null {
  if (!httpUrl || !isMatrixMediaHttpUrl(httpUrl)) {
    return null
  }
  const entry = touchCacheEntry(httpUrl)
  return entry?.blobUrl ?? null
}

export function invalidateMatrixMediaBlobUrl(httpUrl: string): void {
  if (!httpUrl || !isMatrixMediaHttpUrl(httpUrl)) {
    return
  }
  const entry = blobUrlCache.get(httpUrl)
  if (!entry) {
    return
  }
  if (entry.refs > 0) {
    entry.size = -1
    avatarDebugLog('blob · invalidate (deferred, still on screen)', {
      httpUrl: summarizeAvatarUrlForLog(httpUrl),
      refs: entry.refs,
    })
    return
  }
  revokeEntry(httpUrl, entry, 'invalidate')
}

async function fetchAuthorizedMedia(
  requestUrl: string,
  accessToken: string,
): Promise<Response> {
  return fetch(requestUrl, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })
}

export interface FetchMatrixMediaBlobOptions {
  /**
   * When thumbnail fails (400/404), retry as full `/media/download`.
   * Default true for attachments; avatars must pass false (avoids multi‑MB avatar thrash).
   */
  allowFullDownloadFallback?: boolean
}

async function downloadMatrixMediaBlob(
  httpUrl: string,
  options?: FetchMatrixMediaBlobOptions,
): Promise<{ blobUrl: string; size: number }> {
  // Проактивный refresh — иначе после долгого idle первый fetch идёт со старым JWT.
  let session = await ensureMatrixSession()
  if (!session?.accessToken) {
    throw new Error('Нет сессии чата для загрузки медиа')
  }

  let requestUrl = rewriteMatrixHomeserverUrl(httpUrl)
  const allowFullDownloadFallback = options?.allowFullDownloadFallback !== false

  let response = await fetchAuthorizedMedia(requestUrl, session.accessToken)

  if (response.status === 401 || response.status === 403) {
    try {
      session = await refreshMatrixAccessToken()
    } catch {
      session = await ensureMatrixSession({ force: true })
      if (!session) {
        throw new Error('Не удалось обновить сессию чата для медиа')
      }
    }

    response = await fetchAuthorizedMedia(requestUrl, session.accessToken)
  }

  // Some media cannot be thumbnailed (video / missing / unsupported) → 400.
  // Retry once as full download; blob is still parked under the original cache key.
  // Avatars skip this — a failed 96×96 must not pull the whole asset.
  if (
    allowFullDownloadFallback
    && !response.ok
    && (response.status === 400 || response.status === 404)
    && requestUrl.includes('/media/thumbnail/')
  ) {
    const downloadUrl = rewriteMatrixHomeserverUrl(rewriteMatrixThumbnailToDownload(httpUrl))
    if (downloadUrl !== requestUrl) {
      avatarDebugLog('blob · thumbnail failed · retry download', {
        httpUrl: summarizeAvatarUrlForLog(httpUrl),
        status: response.status,
        downloadUrl: summarizeAvatarUrlForLog(downloadUrl),
      })
      requestUrl = downloadUrl
      response = await fetchAuthorizedMedia(requestUrl, session.accessToken)
    }
  }

  if (!response.ok) {
    throw new Error(`Не удалось загрузить медиа (${response.status})`)
  }

  const blob = await response.blob()
  if (!blob.size) {
    throw new Error('Пустой ответ медиа-сервера')
  }

  return {
    blobUrl: URL.createObjectURL(blob),
    size: blob.size,
  }
}

export async function fetchMatrixMediaBlobUrl(
  httpUrl: string,
  options?: FetchMatrixMediaBlobOptions,
): Promise<string> {
  if (!isMatrixMediaHttpUrl(httpUrl)) {
    return httpUrl
  }

  const startedAt = performance.now()
  const cached = touchCacheEntry(httpUrl)
  if (cached) {
    cached.refs += 1
    avatarDebugLog('blob · cache hit', {
      httpUrl: summarizeAvatarUrlForLog(httpUrl),
      refs: cached.refs,
      size: cached.size,
      ms: Math.round(performance.now() - startedAt),
    })
    return cached.blobUrl
  }

  let pending = blobUrlPending.get(httpUrl)
  const joinedPending = Boolean(pending)
  if (!pending) {
    avatarDebugLog('blob · download start', {
      httpUrl: summarizeAvatarUrlForLog(httpUrl),
      cacheSize: blobUrlCache.size,
      allowFullDownloadFallback: options?.allowFullDownloadFallback !== false,
    })
    pending = downloadMatrixMediaBlob(httpUrl, options).then(({ blobUrl, size }) => {
      avatarDebugLog('blob · download done', {
        httpUrl: summarizeAvatarUrlForLog(httpUrl),
        size,
        ms: Math.round(performance.now() - startedAt),
      })
      return parkBlobUrl(httpUrl, blobUrl, size)
    })
    blobUrlPending.set(httpUrl, pending)
    void pending.catch(() => undefined)
    pending.finally(() => {
      if (blobUrlPending.get(httpUrl) === pending) {
        blobUrlPending.delete(httpUrl)
      }
    })
  } else {
    avatarDebugLog('blob · join in-flight', {
      httpUrl: summarizeAvatarUrlForLog(httpUrl),
    })
  }

  try {
    await pending
  } catch (error) {
    avatarDebugLog('blob · download error', {
      httpUrl: summarizeAvatarUrlForLog(httpUrl),
      ms: Math.round(performance.now() - startedAt),
      error: error instanceof Error ? error.message : String(error),
      joinedPending,
    })
    throw error
  }

  const parked = blobUrlCache.get(httpUrl)
  if (!isUsableEntry(parked)) {
    avatarDebugLog('blob · miss after download (evicted/stale) · retry', {
      httpUrl: summarizeAvatarUrlForLog(httpUrl),
      ms: Math.round(performance.now() - startedAt),
    })
    return fetchMatrixMediaBlobUrl(httpUrl, options)
  }

  const retained = retainBlobUrl(httpUrl, parked.blobUrl)
  const ms = Math.round(performance.now() - startedAt)
  avatarDebugLog(ms >= 800 ? 'blob · slow fetch' : 'blob · fetch done', {
    httpUrl: summarizeAvatarUrlForLog(httpUrl),
    refs: parked.refs,
    size: parked.size,
    noIdlePark: Boolean(parked.noIdlePark),
    ms,
    joinedPending,
  })
  return retained
}

export async function resolveMediaUrlForDisplay(fileUrl: string): Promise<string> {
  if (!fileUrl) {
    return fileUrl
  }

  let httpUrl = fileUrl

  if (fileUrl.startsWith('mxc://') || fileUrl.startsWith('mxc:')) {
    const { getMatrixClient } = await import('@/modules/chat-matrix-fast/services/matrixClientLifecycle')
    httpUrl = toAuthenticatedMatrixMediaUrl(getMatrixClient(), fileUrl)
  } else if (isMatrixMediaHttpUrl(fileUrl)) {
    httpUrl = rewriteMatrixMediaHttpUrl(fileUrl)
  } else {
    return fileUrl
  }

  return fetchMatrixMediaBlobUrl(httpUrl)
}

/**
 * Release an on-screen consumer. Blob stays parked (LRU) so chat switches
 * reuse it without network. Eviction only under idle caps / clear / invalidate.
 */
export function releaseMatrixMediaBlobUrl(httpUrl: string): void {
  if (!httpUrl || !isMatrixMediaHttpUrl(httpUrl)) {
    return
  }

  const entry = blobUrlCache.get(httpUrl)
  if (!entry) {
    return
  }

  entry.refs = Math.max(0, entry.refs - 1)
  if (entry.refs > 0) {
    return
  }

  if (entry.size < 0) {
    revokeEntry(httpUrl, entry, 'release-after-invalidate')
    return
  }

  // Oversized / marked noIdlePark — never park (avoids LRU thrash + re-download).
  if (entry.noIdlePark || entry.size > MAX_CACHEABLE_BLOB_BYTES) {
    revokeEntry(httpUrl, entry, 'release-oversized')
    return
  }

  avatarDebugLog('blob · park idle', {
    httpUrl: summarizeAvatarUrlForLog(httpUrl),
    size: entry.size,
    cacheSize: blobUrlCache.size,
  })
  touchCacheEntry(httpUrl)
  evictIdleIfNeeded()
}

export function clearMatrixMediaBlobCache(): void {
  avatarDebugLog('blob · clear all', { cacheSize: blobUrlCache.size })
  for (const [httpUrl, entry] of [...blobUrlCache.entries()]) {
    URL.revokeObjectURL(entry.blobUrl)
    blobUrlCache.delete(httpUrl)
  }
  blobUrlPending.clear()
}
